import { FieldDefinition, FIELD_PATH_SEPARATOR, isObjectArray } from '../types/FieldDefinition';
import { FieldMappingEntry } from '../types/DataSyncViewModel';

/**
 * Represents a node in the field mapping tree
 */
export interface FieldMappingTreeNode {
  /** The field name (e.g., "first_name", "employee_profile") */
  name: string;

  /** Display name for UI */
  displayName: string;

  /** Full path from root (e.g., "employee_profile/first_name") */
  path: string;

  /** Field type (e.g., "string", "object", "integer") */
  type: string;

  /** For array types, the type of items in the array (e.g., "string", "number") */
  arrayItemType?: string;

  /** Whether this field is required */
  required: boolean;

  /** Description of the field */
  description?: string;

  /** Children nodes (for object types) */
  children: FieldMappingTreeNode[];

  /** Whether this is a leaf node (no children) */
  isLeaf: boolean;

  /** Current source field mapping (if any) */
  sourceField?: string;

  /** Whether this mapping is disabled */
  disabled?: boolean;

  /** Whether this mapping was created by auto-mapping */
  isAutoMapped?: boolean;

  /** Whether this mapping was created by cloning the source schema */
  isCloned?: boolean;

  /** Nesting level (0 = root, 1 = first level, etc.) */
  level: number;
}

/**
 * Builds a tree structure from FieldDefinition array
 * @param fields Array of field definitions
 * @param parentPath Parent path for nested fields
 * @param level Nesting level
 * @returns Array of tree nodes
 */
export function buildTreeFromFieldDefinitions(
  fields: FieldDefinition[],
  parentPath: string = '',
  level: number = 0
): FieldMappingTreeNode[] {
  return fields.map((field) => {
    const path = parentPath ? `${parentPath}${FIELD_PATH_SEPARATOR}${field.name}` : field.name;

    // Check if field has children (either object_schema or array of objects)
    const hasObjectChildren = !!(field.object_schema?.fields && field.object_schema.fields.length > 0);
    const hasArrayChildren = !!(field.array && isObjectArray(field.array) && field.array.fields.length > 0);
    const hasChildren = hasObjectChildren || hasArrayChildren;

    const node: FieldMappingTreeNode = {
      name: field.name,
      displayName: field.display_name || field.name,
      path,
      type: field.type || field.primitive_type || 'unknown',
      arrayItemType: field.array && !isObjectArray(field.array) ? field.array.type : undefined,
      required: field.required,
      description: field.description,
      children: [],
      isLeaf: !hasChildren,
      level,
    };

    // Recursively build children for object types
    if (hasObjectChildren) {
      node.children = buildTreeFromFieldDefinitions(
        field.object_schema!.fields,
        path,
        level + 1
      );
    } else if (hasArrayChildren && isObjectArray(field.array!)) {
      node.children = buildTreeFromFieldDefinitions(
        field.array!.fields,
        path,
        level + 1
      );
    }

    return node;
  });
}

/**
 * Enriches tree nodes with existing field mappings
 * @param treeNodes Array of tree nodes
 * @param mappings Array of field mapping entries
 * @returns Tree nodes with mappings applied
 */
export function enrichTreeWithMappings(
  treeNodes: FieldMappingTreeNode[],
  mappings: FieldMappingEntry[]
): FieldMappingTreeNode[] {
  // Create a map for quick lookup by destination path
  const mappingMap = new Map<string, FieldMappingEntry>();
  mappings.forEach((mapping) => {
    mappingMap.set(mapping.destinationField, mapping);
  });

  // Recursively apply mappings to nodes
  function applyMappings(nodes: FieldMappingTreeNode[]): FieldMappingTreeNode[] {
    return nodes.map((node) => {
      const mapping = mappingMap.get(node.path);

      const enrichedNode: FieldMappingTreeNode = {
        ...node,
        sourceField: mapping?.sourceField,
        disabled: mapping?.disabled,
        isAutoMapped: mapping?.isAutoMapped,
        isCloned: mapping?.isCloned,
      };

      // Recursively enrich children
      if (node.children.length > 0) {
        enrichedNode.children = applyMappings(node.children);
      }

      return enrichedNode;
    });
  }

  return applyMappings(treeNodes);
}

/**
 * Flattens a tree structure back into FieldMappingEntry array
 * Only includes leaf nodes that have a source field mapping
 * @param treeNodes Array of tree nodes
 * @returns Array of field mapping entries
 */
export function flattenTreeToMappings(
  treeNodes: FieldMappingTreeNode[]
): FieldMappingEntry[] {
  const result: FieldMappingEntry[] = [];

  function traverse(nodes: FieldMappingTreeNode[]) {
    nodes.forEach((node) => {
      // Only add leaf nodes with source field mappings
      if (node.isLeaf && node.sourceField) {
        result.push({
          destinationField: node.path,
          sourceField: node.sourceField,
          disabled: node.disabled || false,
          isAutoMapped: node.isAutoMapped,
          isCloned: node.isCloned,
        });
      }

      // Recursively traverse children
      if (node.children.length > 0) {
        traverse(node.children);
      }
    });
  }

  traverse(treeNodes);
  return result;
}

/**
 * Finds a node in the tree by its path
 * @param treeNodes Array of tree nodes
 * @param path Path to search for
 * @returns The found node or undefined
 */
export function findNodeByPath(
  treeNodes: FieldMappingTreeNode[],
  path: string
): FieldMappingTreeNode | undefined {
  for (const node of treeNodes) {
    if (node.path === path) {
      return node;
    }

    if (node.children.length > 0) {
      const found = findNodeByPath(node.children, path);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
}

/**
 * Updates a specific node's source field mapping
 * @param treeNodes Array of tree nodes
 * @param path Path of the node to update
 * @param sourceField New source field value
 * @returns Updated tree nodes
 */
export function updateNodeSourceField(
  treeNodes: FieldMappingTreeNode[],
  path: string,
  sourceField: string
): FieldMappingTreeNode[] {
  return treeNodes.map((node) => {
    if (node.path === path) {
      return { ...node, sourceField };
    }

    if (node.children.length > 0) {
      return {
        ...node,
        children: updateNodeSourceField(node.children, path, sourceField),
      };
    }

    return node;
  });
}
